<template>
  <div class="kimi-chat">
    <!-- Chat Button -->
    <button
      v-if="!isOpen"
      class="kimi-chat__button"
      @click="openChat"
      aria-label="Kimi Chat öffnen"
    >
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
      </svg>
    </button>

    <!-- Chat Window -->
    <transition name="chat-window">
      <div v-if="isOpen" class="kimi-chat__window">
        <!-- Chat Header -->
        <div class="kimi-chat__header">
          <div class="kimi-chat__header-content">
            <div class="kimi-chat__avatar">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                <circle cx="12" cy="7" r="4" />
              </svg>
            </div>
            <div class="kimi-chat__header-info">
              <h3 class="kimi-chat__title">Kimi</h3>
              <span class="kimi-chat__subtitle">Dein persönlicher Copilot</span>
            </div>
          </div>
          <button
            class="kimi-chat__close-btn"
            @click="closeChat"
            aria-label="Chat schließen"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <!-- Chat Messages -->
        <div class="kimi-chat__messages" ref="messagesContainer">
          <div
            v-for="(message, index) in messages"
            :key="index"
            class="kimi-chat__message"
            :class="`kimi-chat__message--${message.type}`"
          >
            <div class="kimi-chat__message-bubble">
              <p class="kimi-chat__message-text">{{ message.text }}</p>
              <span class="kimi-chat__message-time">{{ formatTime(message.timestamp) }}</span>
            </div>
          </div>
          <div v-if="isLoading" class="kimi-chat__message kimi-chat__message--kimi">
            <div class="kimi-chat__message-bubble">
              <div class="kimi-chat__typing-indicator">
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
          </div>
        </div>

        <!-- Chat Input -->
        <div class="kimi-chat__input-wrapper">
          <input
            v-model="inputMessage"
            type="text"
            class="kimi-chat__input"
            placeholder="Schreibe eine Nachricht..."
            @keyup.enter="sendMessage"
            @keyup.esc="closeChat"
            :disabled="isLoading"
            ref="chatInput"
          />
          <button
            class="kimi-chat__send-btn"
            @click="sendMessage"
            :disabled="!inputMessage.trim() || isLoading"
            aria-label="Nachricht senden"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="22" y1="2" x2="11" y2="13" />
              <polygon points="22 2 15 22 11 13 2 9 22 2" />
            </svg>
          </button>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { sendMessageToKimi } from "@/services/kimiChatService";

/**
 * KimiChat - Globaler Chat-Bot "Kimi"
 * 
 * @description Schwebender Chat-Button unten rechts, der sich zu einem Chat-Fenster öffnet.
 * Ermöglicht dem User Fragen zu stellen und erhält Antworten von der KI "Kimi".
 */
export default {
  name: "KimiChat",
  data() {
    return {
      isOpen: false,
      inputMessage: "",
      isLoading: false,
      messages: [
        {
          type: "kimi",
          text: "Hallo ich bin Kimi dein persönlicher Copilot. Wie kann ich dir helfen?",
          timestamp: new Date(),
        },
      ],
    };
  },
  watch: {
    isOpen(newVal) {
      if (newVal) {
        this.$nextTick(() => {
          this.scrollToBottom();
          this.$refs.chatInput?.focus();
        });
      }
    },
    messages: {
      deep: true,
      handler() {
        this.$nextTick(() => {
          this.scrollToBottom();
        });
      },
    },
  },
  methods: {
    /**
     * Öffnet den Chat
     */
    openChat() {
      this.isOpen = true;
    },
    /**
     * Schließt den Chat
     */
    closeChat() {
      this.isOpen = false;
    },
    /**
     * Sendet eine Nachricht an Kimi
     */
    async sendMessage() {
      const message = this.inputMessage.trim();
      if (!message || this.isLoading) return;

      // User-Nachricht hinzufügen
      this.messages.push({
        type: "user",
        text: message,
        timestamp: new Date(),
      });

      // Input leeren
      this.inputMessage = "";
      this.isLoading = true;

      try {
        // Nachricht an Kimi senden
        const response = await sendMessageToKimi(message);
        
        // Kimi-Antwort hinzufügen
        this.messages.push({
          type: "kimi",
          text: response,
          timestamp: new Date(),
        });
      } catch (error) {
        console.error("[KimiChat] Fehler beim Senden der Nachricht:", error);
        this.messages.push({
          type: "kimi",
          text: "Entschuldigung, es ist ein Fehler aufgetreten. Bitte versuche es später erneut.",
          timestamp: new Date(),
        });
      } finally {
        this.isLoading = false;
        this.$nextTick(() => {
          this.$refs.chatInput?.focus();
        });
      }
    },
    /**
     * Scrollt zum unteren Rand der Nachrichtenliste
     */
    scrollToBottom() {
      const container = this.$refs.messagesContainer;
      if (container) {
        container.scrollTop = container.scrollHeight;
      }
    },
    /**
     * Formatiert einen Zeitstempel als Uhrzeit
     * @param {Date} timestamp - Zeitstempel
     * @returns {string} Formatierte Uhrzeit
     */
    formatTime(timestamp) {
      return timestamp.toLocaleTimeString("de-DE", {
        hour: "2-digit",
        minute: "2-digit",
      });
    },
  },
};
</script>

<style scoped>
.kimi-chat {
  position: fixed;
  bottom: 24px;
  right: 24px;
  z-index: 1000;
}

/* Chat Button */
.kimi-chat__button {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: linear-gradient(135deg, #005F73 0%, #007A8C 100%);
  border: none;
  color: #ffffff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(0, 95, 115, 0.4);
  transition: all 0.3s ease;
}

.kimi-chat__button:hover {
  transform: scale(1.1);
  box-shadow: 0 6px 16px rgba(0, 95, 115, 0.5);
}

.kimi-chat__button:active {
  transform: scale(0.95);
}

.kimi-chat__button svg {
  width: 24px;
  height: 24px;
}

/* Chat Window */
.kimi-chat__window {
  width: 380px;
  height: 500px;
  max-height: calc(100vh - 48px);
  background: #1E1E1E;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* Chat Header */
.kimi-chat__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  background: rgba(0, 95, 115, 0.1);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.kimi-chat__header-content {
  display: flex;
  align-items: center;
  gap: 12px;
}

.kimi-chat__avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg, #005F73 0%, #007A8C 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
}

.kimi-chat__avatar svg {
  width: 20px;
  height: 20px;
}

.kimi-chat__header-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.kimi-chat__title {
  font-size: 16px;
  font-weight: 600;
  color: #F0F0F0;
  margin: 0;
}

.kimi-chat__subtitle {
  font-size: 12px;
  color: #A0A0A0;
}

.kimi-chat__close-btn {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: transparent;
  border: none;
  color: #A0A0A0;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}

.kimi-chat__close-btn:hover {
  background: rgba(255, 255, 255, 0.1);
  color: #F0F0F0;
}

.kimi-chat__close-btn svg {
  width: 18px;
  height: 18px;
}

/* Chat Messages */
.kimi-chat__messages {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.kimi-chat__messages::-webkit-scrollbar {
  width: 6px;
}

.kimi-chat__messages::-webkit-scrollbar-track {
  background: transparent;
}

.kimi-chat__messages::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.2);
  border-radius: 3px;
}

.kimi-chat__messages::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 255, 255, 0.3);
}

.kimi-chat__message {
  display: flex;
  width: 100%;
}

.kimi-chat__message--user {
  justify-content: flex-end;
}

.kimi-chat__message--kimi {
  justify-content: flex-start;
}

.kimi-chat__message-bubble {
  max-width: 75%;
  padding: 12px 16px;
  border-radius: 16px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.kimi-chat__message--user .kimi-chat__message-bubble {
  background: linear-gradient(135deg, #005F73 0%, #007A8C 100%);
  border-bottom-right-radius: 4px;
}

.kimi-chat__message--kimi .kimi-chat__message-bubble {
  background: rgba(255, 255, 255, 0.1);
  border-bottom-left-radius: 4px;
}

.kimi-chat__message-text {
  font-size: 14px;
  line-height: 1.5;
  color: #F0F0F0;
  margin: 0;
  word-wrap: break-word;
}

.kimi-chat__message-time {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.5);
  align-self: flex-end;
}

/* Typing Indicator */
.kimi-chat__typing-indicator {
  display: flex;
  gap: 4px;
  padding: 4px 0;
}

.kimi-chat__typing-indicator span {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.5);
  animation: typing 1.4s infinite;
}

.kimi-chat__typing-indicator span:nth-child(2) {
  animation-delay: 0.2s;
}

.kimi-chat__typing-indicator span:nth-child(3) {
  animation-delay: 0.4s;
}

@keyframes typing {
  0%, 60%, 100% {
    transform: translateY(0);
    opacity: 0.5;
  }
  30% {
    transform: translateY(-8px);
    opacity: 1;
  }
}

/* Chat Input */
.kimi-chat__input-wrapper {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 16px 20px;
  background: rgba(0, 0, 0, 0.2);
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.kimi-chat__input {
  flex: 1;
  padding: 10px 16px;
  font-size: 14px;
  font-family: inherit;
  color: #F0F0F0;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 20px;
  outline: none;
  transition: all 0.2s ease;
}

.kimi-chat__input:focus {
  border-color: #005F73;
  background: rgba(255, 255, 255, 0.08);
}

.kimi-chat__input::placeholder {
  color: #A0A0A0;
}

.kimi-chat__input:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.kimi-chat__send-btn {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg, #005F73 0%, #007A8C 100%);
  border: none;
  color: #ffffff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.kimi-chat__send-btn:hover:not(:disabled) {
  transform: scale(1.1);
  box-shadow: 0 4px 12px rgba(0, 95, 115, 0.4);
}

.kimi-chat__send-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.kimi-chat__send-btn svg {
  width: 18px;
  height: 18px;
}

/* Transitions */
.chat-window-enter-active,
.chat-window-leave-active {
  transition: all 0.3s ease;
}

.chat-window-enter,
.chat-window-leave-to {
  opacity: 0;
  transform: translateY(20px) scale(0.95);
}

/* Responsive */
@media (max-width: 768px) {
  .kimi-chat {
    bottom: 16px;
    right: 16px;
  }

  .kimi-chat__window {
    width: calc(100vw - 32px);
    height: calc(100vh - 32px);
    max-height: calc(100vh - 32px);
    border-radius: 16px 16px 0 0;
  }

  .kimi-chat__button {
    width: 48px;
    height: 48px;
  }

  .kimi-chat__button svg {
    width: 20px;
    height: 20px;
  }
}
</style>
